import { Button, type ButtonProps, Tooltip } from '@sr/ui-library';
import { Link, useLocation, useParams } from 'react-router-dom';
import { RequestMembershipModal } from '../../../../components/RequestMembershipModal';
import { useMsr2AddTitleAvailable } from '../../../../hooks/useMsr2AddTitleAvailable';
import { useRoyaltiesRegistrationGate } from '../../../../hooks/useRoyaltiesRegistrationGate';
import { buildAddTitleEmbedWorkflowUrl } from '../../../../utils/embedWorkflow';

const REGISTRATION_UNAVAILABLE_TOOLTIP =
  'Title registration is temporarily unavailable. Please try again shortly.';

const DisabledWithReason = ({
  label,
  reason,
  variant,
}: Pick<ButtonProps, 'variant'> & { label: string; reason: string }) => (
  <Tooltip content={reason}>
    {/* A disabled button emits no pointer events, so the tooltip needs a
        focusable wrapper to hover and tab to. */}
    <span tabIndex={0}>
      <Button disabled variant={variant}>
        {label}
      </Button>
    </span>
  </Tooltip>
);

/** Register Title as an explained dead end, for every state that cannot open it. */
const UnavailableRegisterTitle = ({ reason }: { reason: string }) => (
  <DisabledWithReason label="Register Title" reason={reason} />
);

/**
 * Action for the "Add Secondary Royalties" card: registration for current
 * members, the membership application for users who may act on the workspace's
 * behalf, and an explained disabled button for everyone else.
 */
export const RegisterTitleAction = () => {
  const { workspaceId } = useParams();
  const { search, state } = useLocation();
  const gate = useRoyaltiesRegistrationGate();
  // Only members can reach the embed, so the probe stays idle for everyone else.
  const msr2AddTitleQuery = useMsr2AddTitleAvailable(
    gate.status === 'allowed' ? workspaceId : undefined,
  );

  const registerTitleUrl = buildAddTitleEmbedWorkflowUrl(workspaceId, search);

  if (gate.status === 'pending') {
    return (
      <Button disabled loading>
        Register Title
      </Button>
    );
  }

  if (gate.status === 'allowed') {
    // `isLoading`, not `isPending`: the probe is skipped for non-members, and a
    // skipped query stays pending forever.
    if (msr2AddTitleQuery.isLoading) {
      return (
        <Button disabled loading>
          Register Title
        </Button>
      );
    }

    // MSR2 owns the registration form; without it there is nothing to open.
    if (msr2AddTitleQuery.data === true && registerTitleUrl) {
      return (
        <Button asChild>
          {/* Replaces this entry so Back does not re-open the form, and carries
              the page's state so it still knows where Add Title was opened
              from once the workflow closes. */}
          <Link replace state={state} to={registerTitleUrl}>
            Register Title
          </Link>
        </Button>
      );
    }

    return (
      <UnavailableRegisterTitle reason={REGISTRATION_UNAVAILABLE_TOOLTIP} />
    );
  }

  if (gate.status === 'apply') {
    return (
      <RequestMembershipModal
        // This is the card's primary call to action, so it overrides the small
        // size the modal uses where it sits beside other MSR3 actions. The
        // outline treatment is already the modal's own MSR3 default.
        triggerButtonProps={{ size: 'default' }}
        workspace={gate.workspace}
      />
    );
  }

  if (gate.status === 'incomplete') {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <DisabledWithReason
          label="Apply for Membership"
          reason={gate.reason}
          variant="outline"
        />
        <Button asChild>
          <Link to={`/workspace/${workspaceId}/manage-workspace/details`}>
            View Workspace Details
          </Link>
        </Button>
      </div>
    );
  }

  return <UnavailableRegisterTitle reason={gate.reason} />;
};
